import { HighChartBuilder } from '../builders/highchart.builder';
import { FindIndicatorDataResultModel } from '../models/data-point/find-indicator-points.model';
import { TIIndicatorDataModel } from '../models/data-point/ti-indicator-data.model';
import { IndicatorOptionModel } from '../models/drop-down-list/indicator-option.model';
import { IndicatorType } from "../models/enum/indicator-type.enum";
import { FormatHelpers } from './format-helpers';

export class IndicatorHelpers {
    public static findIndicatorPoints(indicatorsData: Array<TIIndicatorDataModel>, option: IndicatorOptionModel, xValue: number): Array<FindIndicatorDataResultModel> {
        const indicatorData = indicatorsData.find((x: TIIndicatorDataModel) => IndicatorHelpers.getIndicatorType(x.name) == option.value && x.name == option.name);
        if (!indicatorData) {
            return [];
        }

        const result: Array<FindIndicatorDataResultModel> = [];
        for (let i = 0; i < indicatorData.points.length; i++) {
            if (indicatorData.points[i].xValue === xValue) {
                result.push(new FindIndicatorDataResultModel({ name: indicatorData.abbreviatedName, value: indicatorData.points[i].value, color: indicatorData.points[i].color }));
            }
        }

        if (option.value == IndicatorType.MACD || option.value == IndicatorType.DonchianChanel || option.value == IndicatorType.BBands) {
            for (const child of indicatorData.childIndicators) {
                for (let i = 0; i < child.points.length; i++) {
                    if (child.points[i].xValue === xValue) {
                        result.push(new FindIndicatorDataResultModel({ name: child.abbreviatedName, value: child.points[i].value, color: child.points[i].color }));
                    }
                }
            }
        }

        return result;
    }

    public static getIndicatorType(name: string): IndicatorType | null {
        if (name.indexOf("AVG_VOLUME") > -1)
            return 2;
        else if (name.indexOf("EMACLOUD") > -1)
            return 28;
        else if (name.indexOf("VOLUME") > -1)
            return 1;
        else if (name.indexOf("ADX") > -1)
            return 3;
        else if (name.indexOf("ATR") > -1)
            return 4;
        else if (name.indexOf("RSI") > -1)
            return 5;
        else if (name.indexOf("Dollar") > -1)
            return 6;
        else if (name.indexOf("DC") > -1)
            return 7;
        else if (name.indexOf("MONEY") > -1)
            return 8;
        else if (name.indexOf("EMA") > -1)
            return 9;
        else if (name.indexOf("SMA") > -1)
            return 10;
        else if (name.indexOf("MACD") > -1)
            return 11;
        else if (name.indexOf("MP HIGH") > -1)
            return 12;
        else if (name.indexOf("MP LOW") > -1)
            return 13;
        else if (name.indexOf("Pivot") > -1)
            return 14;
        else if (name.indexOf("ROC") > -1)
            return 15;
        else if (name.indexOf("REL") > -1)
            return 16;
        else if (name.indexOf("Distance") > -1)
            return 17;
        else if (name.indexOf("STOCH") > -1)
            return 18;
        else if (name.indexOf("TI_SCORE") > -1)
            return 19;
        else if (name.indexOf("_VWAP") > -1 || name.toUpperCase() === 'VWAP')
            return 20;
        else if (name.indexOf("BB_T") > -1)
            return 21;
        else if (name.indexOf("BB_W") > -1)
            return 22;
        else if (name.indexOf("Draw") > -1)
            return 23;
        else if (name.indexOf("KELTNER") > -1)
            return 24;
        else if (name.indexOf("PAT_REC") > -1)
            return 25;
        else if (name.indexOf("Williams") > -1)
            return 26;
        else if (name.indexOf("AVWAP") > -1)
            return 27;

        return null;
    }

    public static getIndicatorHighChartConfig(indicatorType: IndicatorType): any {
        if (!indicatorType) {
            return null;
        }

        switch (indicatorType) {
            case IndicatorType.EMACloud: {
                return IndicatorHelpers.getEMACloudConfig();
            }
            case IndicatorType.Volume: {
                return IndicatorHelpers.getVolumeConfig();
            }
            case IndicatorType.MACD: {
                return IndicatorHelpers.getMACDConfig();
            }
            case IndicatorType.AverageVolume: {
                return IndicatorHelpers.getLineSerieConfig('avg_volume');
            }
            case IndicatorType.AverageDirectionIndex: {
                return IndicatorHelpers.getLineSerieConfig('avg_direction_index');
            }
            case IndicatorType.RSI: {
                return IndicatorHelpers.getLineSerieConfig('rsi');
            }
            case IndicatorType.AverageTrueRange: {
                return IndicatorHelpers.getLineSerieConfig('avg_true_range');
            }
            case IndicatorType.DollarVolume: {
                return IndicatorHelpers.getColumnSerieConfig('dollar_volume');
            }
            case IndicatorType.DonchianChanel: {
                return IndicatorHelpers.getDonchianChanelConfig();
            }
            case IndicatorType.MoneyFlowIndex: {
                return IndicatorHelpers.getLineSerieConfig('money_flow_index');
            }
            case IndicatorType.MovingAverageEMA: {
                return IndicatorHelpers.getLineSerieConfig('ema');
            }
            case IndicatorType.MovingAverageSMA: {
                return IndicatorHelpers.getLineSerieConfig('sma');
            }
            case IndicatorType.MultiPeriodHigh: {
                return IndicatorHelpers.getLineSerieConfig('multi-period-high');
            }
            case IndicatorType.MultiPeriodLow: {
                return IndicatorHelpers.getLineSerieConfig('multi-period-low');
            }
            case IndicatorType.PivotPoint: {
                return IndicatorHelpers.getLineSerieConfig('pivot-point');
            }
            case IndicatorType.RateOfChange: {
                return IndicatorHelpers.getLineSerieConfig('rate_of_change');
            }
            case IndicatorType.RelativeVolume: {
                return IndicatorHelpers.getLineSerieConfig('relative_volume');
            }
            case IndicatorType.DistanceFromEMA: {
                return IndicatorHelpers.getLineSerieConfig('distance_ema');
            }
            case IndicatorType.Stochastics: {
                return IndicatorHelpers.getLineSerieConfig('stochastics');
            }
            case IndicatorType.TIStrength: {
                return IndicatorHelpers.getColumnSerieConfig('ti_strength');
            }
            case IndicatorType.VWAP: {
                return IndicatorHelpers.getLineSerieConfig('vwap');
            }
            case IndicatorType.BBands: {
                return IndicatorHelpers.getBBandsConfig();
            }
            case IndicatorType.BBWidth: {
                return IndicatorHelpers.getLineSerieConfig('bb_width');
            }
            case IndicatorType.Drawdown: {
                return IndicatorHelpers.getLineSerieConfig('drawdown');
            }
            case IndicatorType.KeltnerChannel: {
                return IndicatorHelpers.getKeltnerChannelConfig();
            }
            case IndicatorType.TIPatternRec: {
                return IndicatorHelpers.getColumnSerieConfig('ti_pattern_rec');
            }
            case IndicatorType.WilliamR: {
                return IndicatorHelpers.getLineSerieConfig('william_r');
            }
            case IndicatorType.AVWAP: {
                return IndicatorHelpers.getLineSerieConfig('avwap');
            }
            case IndicatorType.HighChartPivotPoints: {
                return IndicatorHelpers.getHighChartPivotPointsConfig();
            }
            case IndicatorType.HighChartIKH: {
                return IndicatorHelpers.getHighChartIKHConfig();
            }
            case IndicatorType.HighChartAD: {
                return IndicatorHelpers.getHighChartADConfig();
            }
            case IndicatorType.HighChartMFI: {
                return IndicatorHelpers.getHighChartMFIConfig();
            }
            case IndicatorType.HighChartMFI: {
                return IndicatorHelpers.getHighChartMFIConfig();
            }
            case IndicatorType.HighChartVolume: {
                return IndicatorHelpers.getHighChartVolumeConfig();
            }
            case IndicatorType.HighChartCCI: {
                return IndicatorHelpers.getHighChartCCIConfig();
            }
            case IndicatorType.HighChartStochastic: {
                return IndicatorHelpers.getHighChartStochasticConfig();
            }
            default: {
                return null;
            }
        }
    }

    private static getEMACloudConfig(): any {
        return {
            series: [{
                type: 'areasplinerange',
                name: 'ema-cloud-line-1',
                className: 'ema-cloud-line-1',
                data: [],
                colorByPoint: false,
                zoneAxis: 'x',
                lineWidth: 2,
                zIndex: 2,
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            }
            ],
            yAxis: {
                tickColor: '#000',
                gridLineColor: 'rgb(0, 0, 0)',
                gridLineDashStyle: 'Solid',
                gridLineWidth: 0,
                tickPixelInterval: 32,
                crosshair: {
                    className: 'highchart-main-crosshair',
                    dashStyle: 'Dash',
                    snap: false
                },
                labels: {
                    formatter: function () {
                        return '';
                    },
                    style: {
                        color: '#000'
                    }
                }
            }
        };
    }

    private static getVolumeConfig(): any {
        return {
            series: [{
                groupPadding: 0.1,
                // pointPadding: 0.25,
                minPointLength: 4,
                color: '#0000ff',
                type: 'column',
                name: 'volume',
                id: 'volume',
                data: [],
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            }],
            yAxis: {
                tickColor: '#222222',
                gridLineColor: 'rgb(220, 220, 220, 0.6)',
                gridLineDashStyle: 'Dash',
                tickPixelInterval: 30,
                crosshair: {
                    className: 'highchart-main-crosshair',
                    dashStyle: 'Dash',
                    snap: false
                },
                labels: {
                    formatter: function () {
                        const val = FormatHelpers.numberToString(parseFloat(this.value + ''));
                        return val;
                    },
                    style: {
                        color: '#222222'
                    }
                }
            }
        } as any;
    }

    private static getMACDConfig(): any {
        return {
            yAxis: {
                tickColor: '#222222',
                gridLineColor: 'rgb(220, 220, 220, 0.6)',
                gridLineDashStyle: 'Dash',
                tickPixelInterval: 32,
                crosshair: {
                    className: 'highchart-main-crosshair',
                    dashStyle: 'Dash',
                    snap: false
                },
                labels: {
                    formatter: function () {
                        const val = FormatHelpers.numberToString(parseFloat(this.value + ''));
                        return val;
                    },
                    style: {
                        color: '#222222'
                    }
                }
            },
            series: [{
                type: 'column',
                name: 'macd_hist',
                data: [],
                groupPadding: 0.1,
                // pointPadding: 0.25,
                minPointLength: 4,
                color: 'green',
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            }, {
                type: 'spline',
                name: 'macd_line',
                data: [],
                colorByPoint: true,
                zoneAxis: 'x',
                lineWidth: 2,
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            },
            {
                type: 'spline',
                name: 'macd_signal_line',
                data: [],
                colorByPoint: true,
                zoneAxis: 'x',
                lineWidth: 2,
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            }]
        } as any;
    }

    private static getLineSerieConfig(name: string): any {
        return {
            series: [{
                type: 'line',
                name: name,
                lineWidth: 2,
                data: [],
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            }],
            yAxis: {
                tickColor: '#222222',
                gridLineColor: 'rgb(220, 220, 220, 0.6)',
                gridLineDashStyle: 'Dash',
                tickPixelInterval: 32,
                crosshair: {
                    className: 'highchart-main-crosshair',
                    dashStyle: 'Dash',
                    snap: false
                },
                labels: {
                    formatter: function () {
                        const val = FormatHelpers.numberToString(parseFloat(this.value + ''));
                        return val;
                    },
                    style: {
                        color: '#222222'
                    }
                }
            }
        };
    }

    private static getColumnSerieConfig(name: string): any {
        return {
            series: [{
                type: 'column',
                name: name,
                data: [],
                groupPadding: 0.1,
                // pointPadding: 0.25,
                minPointLength: 4,
                color: '#28B463',
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            }],
            yAxis: {
                tickColor: '#222222',
                gridLineColor: 'rgb(220, 220, 220, 0.6)',
                gridLineDashStyle: 'Dash',
                tickPixelInterval: 30,
                crosshair: {
                    className: 'highchart-main-crosshair',
                    dashStyle: 'Dash',
                    snap: false
                },
                labels: {
                    formatter: function () {
                        const val = FormatHelpers.numberToString(parseFloat(this.value + ''));
                        return val;
                    },
                    style: {
                        color: '#222222'
                    }
                }
            }
        } as any;
    }

    private static getDonchianChanelConfig(): any {
        return {
            yAxis: {
                tickColor: '#222222',
                gridLineColor: 'rgb(220, 220, 220, 0.6)',
                gridLineDashStyle: 'Dash',
                tickPixelInterval: 32,
                crosshair: {
                    className: 'highchart-main-crosshair',
                    dashStyle: 'Dash',
                    snap: false
                },
                labels: {
                    formatter: function () {
                        const val = FormatHelpers.numberToString(parseFloat(this.value + ''));
                        return val;
                    },
                    style: {
                        color: '#222222'
                    }
                }
            },
            series: [{
                type: 'spline',
                name: 'dc_top',
                color: 'rgb(45, 74, 255)',
                data: [],
                lineWidth: 2,
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            },
            {
                type: 'spline',
                name: 'dc_bottom',
                data: [],
                color: 'rgb(237, 162, 37)',
                lineWidth: 2,
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            }]
        };
    }

    private static getBBandsConfig(): any {
        return {
            series: [{
                type: 'line',
                name: 'bb_top',
                data: [],
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            },
            {
                type: 'line',
                name: 'bb_bottom',
                data: [],
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            }],
            yAxis: {
                tickColor: '#222222',
                gridLineColor: 'rgb(220, 220, 220, 0.6)',
                gridLineDashStyle: 'Dash',
                tickPixelInterval: 32,
                crosshair: {
                    className: 'highchart-main-crosshair',
                    dashStyle: 'Dash',
                    snap: false
                },
                labels: {
                    formatter: function () {
                        const val = FormatHelpers.numberToString(parseFloat(this.value + ''));
                        return val;
                    },
                    style: {
                        color: '#222222'
                    }
                }
            }
        };
    }

    private static getKeltnerChannelConfig(): any {
        return {
            series: [{
                type: 'line',
                name: 'keltner_top',
                data: [],
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            },
            {
                type: 'line',
                name: 'keltner_bottom',
                data: [],
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            }],
            yAxis: {
                tickColor: '#222222',
                gridLineColor: 'rgb(220, 220, 220, 0.6)',
                gridLineDashStyle: 'Dash',
                tickPixelInterval: 32,
                crosshair: {
                    className: 'highchart-main-crosshair',
                    dashStyle: 'Dash',
                    snap: false
                },
                labels: {
                    formatter: function () {
                        const val = FormatHelpers.numberToString(parseFloat(this.value + ''));
                        return val;
                    },
                    style: {
                        color: '#222222'
                    }
                }
            }
        };
    }

    private static getHighChartPivotPointsConfig(): any {
        return {
            series: [{
                type: 'pivotpoints',
                name: 'highchart-pivotpoints',
                linkedTo: HighChartBuilder.mainChartAreaName,
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            }],
            yAxis: {
            }
        };
    }

    private static getHighChartIKHConfig(): any {
        return {
            series: [{
                type: 'ikh',
                name: 'highchart-ikh',
                linkedTo: HighChartBuilder.mainChartAreaName,
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            }],
            yAxis: {
            }
        };
    }

    private static getHighChartADConfig(): any {
        return {
            series: [{
                type: 'ad',
                name: 'highchart-ad',
                linkedTo: HighChartBuilder.mainChartAreaName,
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            }],
            yAxis: {
                tickColor: '#222222',
                gridLineColor: 'rgb(220, 220, 220, 0.6)',
                gridLineDashStyle: 'Dash',
                tickPixelInterval: 32,
                crosshair: {
                    className: 'highchart-main-crosshair',
                    dashStyle: 'Dash',
                    snap: false
                },
                labels: {
                    formatter: function () {
                        const val = FormatHelpers.numberToString(parseFloat(this.value + ''));
                        return val;
                    },
                    style: {
                        color: '#222222'
                    }
                }
            }
        }
    }

    private static getHighChartMFIConfig(): any {
        return {
            series: [{
                type: 'mfi',
                name: 'highchart-mfi',
                linkedTo: HighChartBuilder.mainChartAreaName,
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            }],
            yAxis: {
                tickColor: '#222222',
                gridLineColor: 'rgb(220, 220, 220, 0.6)',
                gridLineDashStyle: 'Dash',
                tickPixelInterval: 32,
                crosshair: {
                    className: 'highchart-main-crosshair',
                    dashStyle: 'Dash',
                    snap: false
                },
                labels: {
                    formatter: function () {
                        const val = FormatHelpers.numberToString(parseFloat(this.value + ''));
                        return val;
                    },
                    style: {
                        color: '#222222'
                    }
                }
            }
        }
    }

    private static getHighChartVolumeConfig(): any {
        return {
            series: [{
                groupPadding: 0.1,
                // pointPadding: 0.25,
                minPointLength: 4,
                color: '#0000ff',
                type: 'column',
                name: 'volume',
                id: 'volume',
                data: [],
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            }],
            yAxis: {
                tickColor: '#222222',
                gridLineColor: 'rgb(220, 220, 220, 0.6)',
                gridLineDashStyle: 'Dash',
                tickPixelInterval: 32,
                crosshair: {
                    className: 'highchart-main-crosshair',
                    dashStyle: 'Dash',
                    snap: false
                },
                labels: {
                    formatter: function () {
                        const val = FormatHelpers.numberToString(parseFloat(this.value + ''));
                        return val;
                    },
                    style: {
                        color: '#222222'
                    }
                }
            }
        } as any;
    }

    private static getHighChartCCIConfig(): any {
        return {
            series: [{
                type: 'cci',
                name: 'highchart-cci',
                linkedTo: HighChartBuilder.mainChartAreaName,
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            }],
            yAxis: {
                tickColor: '#222222',
                gridLineColor: 'rgb(220, 220, 220, 0.6)',
                gridLineDashStyle: 'Dash',
                tickPixelInterval: 32,
                crosshair: {
                    className: 'highchart-main-crosshair',
                    dashStyle: 'Dash',
                    snap: false
                },
                labels: {
                    formatter: function () {
                        const val = FormatHelpers.numberToString(parseFloat(this.value + ''));
                        return val;
                    },
                    style: {
                        color: '#222222'
                    }
                }
            }
        }
    }

    private static getHighChartStochasticConfig(): any {
        return {
            series: [{
                type: 'stochastic',
                name: 'highchart-stochastic',
                className: 'highchart-stochastic',
                linkedTo: HighChartBuilder.mainChartAreaName,
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            }],
            yAxis: {
                tickColor: '#222222',
                gridLineColor: 'rgb(220, 220, 220, 0.6)',
                gridLineDashStyle: 'Dash',
                tickPixelInterval: 32,
                crosshair: {
                    className: 'highchart-main-crosshair',
                    dashStyle: 'Dash',
                    snap: false
                },
                labels: {
                    formatter: function () {
                        const val = FormatHelpers.numberToString(parseFloat(this.value + ''));
                        return val;
                    },
                    style: {
                        color: '#222222'
                    }
                }
            }
        }
    }
}
